import { FC, useMemo } from "react";
import { useFormik } from "formik";
import { Flex, FormControl, FormLabel, Input as ChakraInput, Select as ChakraSelect, Button, Grid, Box } from "@chakra-ui/react";
import { Select as ChakraReactSelect } from "chakra-react-select";
import { Recruiter } from "@/model/recruiters";
import { CandidateFilters } from "@/model/candidate";

type StatusOption = { value: string; label: string };

type FiltersPopoverProps = {
  recruiters: ReadonlyArray<Recruiter>;
  skillList: ReadonlyArray<StatusOption>;
  sourceList: ReadonlyArray<StatusOption>;
  statusList: ReadonlyArray<StatusOption>;
  values: CandidateFilters;
  onApply: (values: CandidateFilters) => void;
  onReset: () => void;
};

export const FiltersPopover: FC<FiltersPopoverProps> = ({ recruiters, skillList, sourceList, statusList, values, onApply, onReset }) => {
  const formik = useFormik<CandidateFilters>({
    initialValues: values,
    enableReinitialize: true,
    onSubmit: (submittedValues: CandidateFilters) => {
      onApply(submittedValues);
    },
  });

  const selectedSkills = useMemo(() => {
    if (!formik.values.skill) return [];
    const skillArray = formik.values.skill.split(",").filter(Boolean);
    return skillList.filter((s) => skillArray.includes(s.value));
  }, [formik.values.skill, skillList]);

  const handleResetLocal = () => {
    formik.resetForm({
      values: {
        department_id: "",
        skill: "",
        source_id: "",
        status: "",
        created_by: "",
        responsible_recruiter_id: "",
        created_at_from: "",
        created_at_to: "",
      },
    });
    onReset();
  };

  const handleApplyLocal = () => {
    void formik.submitForm();
  };

  return (
    <Box width="600px">
      {/* Ключевые навыки занимают всю ширину */}
      <FormControl>
        <FormLabel>Ключевые навыки</FormLabel>
        <ChakraReactSelect
          isMulti
          placeholder="Ключевые навыки (начните вводить для поиска)"
          value={selectedSkills}
          onChange={(selectedOptions) => {
            const skillValues = selectedOptions ? selectedOptions.map((s) => s.value).join(",") : "";
            formik.setFieldValue("skill", skillValues);
          }}
          options={skillList}
          isSearchable
          isClearable
          closeMenuOnSelect={false}
          hideSelectedOptions={false}
          maxMenuHeight={320}
          menuPlacement="auto"
          chakraStyles={{
            container: (provided) => ({
              ...provided,
              minHeight: "40px",
            }),
            control: (provided, state) => ({
              ...provided,
              borderColor: state.isFocused ? "#3182ce" : "#E2E8F0",
              boxShadow: state.isFocused ? "0 0 0 1px #3182ce" : "none",
              "&:hover": {
                borderColor: state.isFocused ? "#3182ce" : "#CBD5E0",
              },
            }),
            multiValue: (provided) => ({
              ...provided,
              backgroundColor: "#EDF2F7",
              borderRadius: "6px",
            }),
            multiValueLabel: (provided) => ({
              ...provided,
              color: "#2D3748",
              fontSize: "14px",
            }),
            multiValueRemove: (provided) => ({
              ...provided,
              color: "#718096",
              "&:hover": {
                backgroundColor: "#E53E3E",
                color: "white",
              },
            }),
            placeholder: (provided) => ({
              ...provided,
              color: "#A0AEC0",
              fontSize: "14px",
            }),
            menu: (provided) => ({
              ...provided,
              zIndex: 9999,
            }),
            menuList: (provided) => ({
              ...provided,
              maxHeight: "320px",
            }),
            option: (provided, state) => ({
              ...provided,
              fontSize: "14px",
              padding: "8px 12px",
            }),
          }}
          noOptionsMessage={({ inputValue }) => {
            if (!inputValue.trim()) {
              return "Введите текст для поиска навыков";
            }
            return `Навыки "${inputValue}" не найдены`;
          }}
        />
      </FormControl>

      {/* Основные фильтры в 2 колонки */}
      <Grid templateColumns="repeat(2, 1fr)" gap="16px">
        <FormControl>
          <FormLabel>Источник</FormLabel>
          <ChakraSelect
            placeholder="Выберите источник"
            value={formik.values.source_id || ""}
            onChange={(e) => {
              formik.setFieldValue("source_id", e.target.value);
            }}
          >
            {sourceList.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </ChakraSelect>
        </FormControl>

        <FormControl>
          <FormLabel>Статус кандидата</FormLabel>
          <ChakraSelect
            placeholder="Выберите статус"
            value={formik.values.status || ""}
            onChange={(e) => {
              formik.setFieldValue("status", e.target.value);
            }}
          >
            {statusList.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </ChakraSelect>
        </FormControl>

        <FormControl>
          <FormLabel>Создан кем</FormLabel>
          <ChakraSelect
            placeholder="Выберите пользователя"
            value={formik.values.created_by || ""}
            onChange={(e) => {
              formik.setFieldValue("created_by", e.target.value);
            }}
          >
            {recruiters.map((r) => (
              <option key={r.id} value={r.id}>
                {r.username}
              </option>
            ))}
          </ChakraSelect>
        </FormControl>

        <FormControl>
          <FormLabel>Ответственный рекрутер</FormLabel>
          <ChakraSelect
            placeholder="Выберите пользователя"
            value={formik.values.responsible_recruiter_id || ""}
            onChange={(e) => {
              formik.setFieldValue("responsible_recruiter_id", e.target.value);
            }}
          >
            {recruiters.map((r) => (
              <option key={r.id} value={r.id}>
                {r.username}
              </option>
            ))}
          </ChakraSelect>
        </FormControl>
      </Grid>

      {/* Даты в 2 колонки */}
      <Grid templateColumns="repeat(2, 1fr)" gap="16px">
        <FormControl>
          <FormLabel>Дата с</FormLabel>
          <ChakraInput
            type="date"
            value={formik.values.created_at_from || ""}
            onChange={(e) => formik.setFieldValue("created_at_from", e.target.value)}
          />
        </FormControl>
        <FormControl>
          <FormLabel>Дата по</FormLabel>
          <ChakraInput
            type="date"
            value={formik.values.created_at_to || ""}
            onChange={(e) => formik.setFieldValue("created_at_to", e.target.value)}
          />
        </FormControl>
      </Grid>

      <Flex justifyContent="flex-end" gap="8px" mt="12px">
        <Button variant="ghost" onClick={handleResetLocal}>
          Сбросить
        </Button>
        <Button onClick={handleApplyLocal} bg="#2563eb" color="white" _hover={{ bg: "#2563eb" }} _active={{ bg: "#2563eb" }}>
          Применить
        </Button>
      </Flex>
    </Box>
  );
};
